import Client from "fhir-kit-client";
import i18n from "i18next";
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import FhirStructuredViewer from "../../components/viewers/FhirStructuredViewer";
import ImageViewer from "../../components/viewers/ImageViewer";
import PdfViewer from "../../components/viewers/PdfViewer";
import TextDocumentViewer from "../../components/viewers/TextDocumentViewer";
import PapyrusPage from "../../components/PapyrusPage/PapyrusPage";
import UnsupportedViewer from "../../components/viewers/UnsupportedViewer";
import DocumentAttachmentService, { ResolvedDocumentAttachment } from "../../services/document/DocumentAttachmentService";
import { PapyrusDocumentRepresentation } from "../../services/document/DocumentModels";
import DocumentReferenceService from "../../services/document/DocumentReferenceService";

const DocumentViewerPage: FunctionComponent = () => {
    const { documentReferenceId, contentIndex } = useParams();
    const [representation, setRepresentation] = useState<PapyrusDocumentRepresentation | undefined>();
    const [resolvedAttachment, setResolvedAttachment] = useState<ResolvedDocumentAttachment | undefined>();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const fhirClient = useMemo(() => new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir"
    }), []);

    const navigate = useNavigate();

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    useEffect(() => {
        loadPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentReferenceId, contentIndex]);

    useEffect(() => {
        return () => {
            if (resolvedAttachment?.objectUrl) {
                URL.revokeObjectURL(resolvedAttachment.objectUrl);
            }
        };
    }, [resolvedAttachment]);

    async function loadPage() {
        if (!documentReferenceId || contentIndex === undefined) {
            setErrorMessage(i18n.t("error.missingdocumentviewerparameters"));
            return;
        }

        setLoading(true);
        setErrorMessage(undefined);
        setRepresentation(undefined);
        setResolvedAttachment(undefined);

        try {
            const documentReference = await fhirClient.read({
                resourceType: "DocumentReference",
                id: documentReferenceId,
            });

            const selectedRepresentation = DocumentReferenceService.getRepresentation(
                documentReference,
                Number(contentIndex)
            );

            if (!selectedRepresentation) {
                setErrorMessage(i18n.t("error.documentrepresentationnotfound"));
                return;
            }

            const attachment = await DocumentAttachmentService.resolve(selectedRepresentation, fhirClient);
            setRepresentation(selectedRepresentation);
            setResolvedAttachment(attachment);
        } catch (error) {
            onError(error);
        } finally {
            setLoading(false);
        }
    }

    function renderViewer() {
        if (!representation) {
            return null;
        }

        switch (representation.viewerType) {
            case "pdf-viewer":
                return <PdfViewer representation={representation} resolvedAttachment={resolvedAttachment} />;
            case "image-viewer":
                return <ImageViewer representation={representation} resolvedAttachment={resolvedAttachment} />;
            case "text-document-viewer":
                return <TextDocumentViewer representation={representation} resolvedAttachment={resolvedAttachment} />;
            case "structured-fhir-viewer":
                return <FhirStructuredViewer representation={representation} resolvedAttachment={resolvedAttachment} />;
            case "unsupported-viewer":
            default:
                return <UnsupportedViewer representation={representation} />;
        }
    }

    return (
        <PapyrusPage titleKey="title.documentviewer" loading={loading} needsLogin={false}>
            <>
                {errorMessage && <Alert variant="warning">{errorMessage}</Alert>}
                {representation && (
                    <Card className="mb-3">
                        <Card.Body>
                            <Card.Title>{representation.contentType}</Card.Title>
                            <Card.Text>{i18n.t("viewer.type")}: {representation.viewerType}</Card.Text>
                        </Card.Body>
                    </Card>
                )}
                {renderViewer()}
            </>
        </PapyrusPage>
    );
};

export default DocumentViewerPage;
